import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Controller, useFormContext } from 'react-hook-form';
import { Grid, TextField } from '@nextiva/next-ui-components';
import { debounce } from 'lodash';

import { RevealCard } from 'components/RevealCard';
import { actionTypes, CallForwardContext } from 'containers/CallForwarding';
import { HuntGroupService } from 'services';

const BUSY_ENABLED = 'busy.enabled';

export const CallForwardBusy = ({ huntGroupId = '' }) => {
  const { state, dispatch } = useContext(CallForwardContext);
  const {
    clearErrors,
    getValues,
    setError,
    setValue,
    control,
  } = useFormContext();

  const debouncedSetBusyDirty = useMemo(
    () =>
      debounce(
        (value) => {
          const dirty = value !== state.originalFields.busy.phoneNumber;
          if (dirty === state.dirtySections.busy) return; // avoid re-rendering if dirty is unchanged

          dispatch({
            type: actionTypes.DIRTY_UPDATED,
            payload: { sectionName: 'busy', dirty },
          });
        },
        200,
        { trailing: true },
      ),
    [state.originalFields.busy.phoneNumber, state.dirtySections.busy, dispatch],
  );

  const debouncedUpdateBusyEnabled = useMemo(
    () =>
      debounce(
        async () => {
          const checked = getValues(BUSY_ENABLED);

          try {
            await HuntGroupService.toggleCallForwardBusyActive(
              huntGroupId,
              checked,
            );
            dispatch({
              type: actionTypes.SWITCH_TOGGLED,
              payload: { sectionName: 'busy', enabled: checked },
            });
          } catch (error) {
            dispatch({
              type: actionTypes.SNACKBAR_SET,
              payload: {
                message:
                  'There was an error while updating the active status for Call Forwarding (Busy). Please try again later or contact support if the issue persists.',
                type: 'error',
              },
            });

            setValue(BUSY_ENABLED, !checked);
          }
        },
        500,
        { trailing: true },
      ),
    [dispatch, getValues, huntGroupId, setValue],
  );

  const handleMissingFields = () => {
    const phoneNumber = getValues('busy.phoneNumber');
    const payload = phoneNumber
      ? {
          message:
            'You have unsaved changes, click the blue save button to enable this feature.',
          section: 'busy',
          type: 'error',
        }
      : {
          message:
            'Missing required information. Fill out the missing fields and then click the blue "Save" button to enable this feature.',
          section: 'busy',
          type: 'error',
        };

    dispatch({ type: actionTypes.TOGGLE_FAILED, payload });

    if (!phoneNumber)
      setError(
        'busy.phoneNumber',
        { type: 'required', message: 'Phone number is required.' },
        { shouldFocus: true },
      );
  };

  const handleSwitchClick = ({ event, field }) => {
    if (!state.originalFields.busy.phoneNumber) {
      handleMissingFields();
      return;
    }

    field.onChange(event);
    debouncedUpdateBusyEnabled();
  };

  return (
    <RevealCard
      description="Forward incoming calls to a different team's number when your phone is busy."
      handleSwitchClick={handleSwitchClick}
      sectionName="busy"
      title="Call forward (busy)"
      tooltip="Forward all of the team’s incoming calls to a different phone number when they’re busy. A team can be considered busy when they are on do not disturb, or if they get a second call with call waiting disabled." // TODO: ask Nicole for verbiage here -- compare against VS-UI
    >
      <Grid container>
        <Grid className="field-container" item xs={5}>
          <Controller
            control={control}
            defaultValue=""
            name="busy.phoneNumber"
            render={({ field, fieldState }) => (
              <TextField
                fullWidth
                id="busy-phone-number"
                label="PHONE NUMBER"
                inputProps={{ maxLength: 20 }}
                // eslint-disable-next-line react/jsx-no-duplicate-props
                InputProps={{ disableUnderline: true }}
                InputLabelProps={{ shrink: true }}
                {...field}
                onChange={(event) => {
                  if (fieldState.error?.type === 'required')
                    clearErrors('busy.phoneNumber');
                  field.onChange(event);
                  debouncedSetBusyDirty(event.target.value);
                }}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                data-testid="PlatformAdminUI-TeamVoiceSettings-CallForwardBusy-PhoneNumber"
              />
            )}
          />
        </Grid>
      </Grid>
    </RevealCard>
  );
};

CallForwardBusy.propTypes = {
  huntGroupId: PropTypes.string.isRequired,
};
